import React, { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Environment, Lightformer } from "@react-three/drei";
import { WineBottle } from "@/poc/WineBottle";
import { SilhouetteBottles } from "@/poc/SilhouetteBottles";
import { getFrame, getStaticFrame, makeFrameScratch } from "@/poc/keyframes";

const IDLE_SPEED = (Math.PI * 2) / 30; // ~1 revolution per 30s
const TWO_PI = Math.PI * 2;

/* soft elliptical contact shadow — radial gradient texture, artifact-free */
function makeShadowTexture() {
  const c = document.createElement("canvas");
  c.width = c.height = 256;
  const ctx = c.getContext("2d");
  const g = ctx.createRadialGradient(128, 128, 8, 128, 128, 128);
  g.addColorStop(0, "rgba(0,0,0,0.82)");
  g.addColorStop(0.45, "rgba(0,0,0,0.45)");
  g.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 256, 256);
  return new THREE.CanvasTexture(c);
}

const ContactShadow = () => {
  const tex = useMemo(() => makeShadowTexture(), []);
  return (
    <mesh rotation-x={-Math.PI / 2} position-y={0.004}>
      <planeGeometry args={[2.6, 2.6]} />
      <meshBasicMaterial map={tex} transparent depthWrite={false} />
    </mesh>
  );
};

const Rig = ({ progressRef, mouseRef, isMobile, staticMode }) => {
  const bottleRef = useRef();
  const keyRef = useRef();
  const rimRef = useRef();
  const ambRef = useRef();
  const rotState = useRef(0);
  const par = useRef({ x: 0, y: 0 });
  const silOpacity = useRef(0);

  const scratch = useMemo(() => makeFrameScratch(), []);
  const cur = useMemo(() => makeFrameScratch(), []);
  const { invalidate } = useThree();

  // static mode renders on demand — pump a few frames so the transmission
  // buffer and environment settle, then stop
  useEffect(() => {
    if (!staticMode) return;
    let count = 0;
    const id = setInterval(() => {
      invalidate();
      if (++count > 24) clearInterval(id);
    }, 90);
    return () => clearInterval(id);
  }, [staticMode, invalidate]);

  useFrame((state, delta) => {
    const dt = Math.min(delta, 0.05);
    const { camera, scene } = state;

    if (staticMode) {
      getStaticFrame(isMobile, cur);
      camera.position.copy(cur.cam);
      camera.lookAt(cur.look);
      if (bottleRef.current) {
        bottleRef.current.rotation.y = cur.rot;
        bottleRef.current.position.x = 0;
      }
    } else {
      const p = progressRef.current;
      getFrame(p, isMobile, scratch);

      // frame damping — kills scroll jitter in both directions
      const k = 1 - Math.exp(-4.2 * dt);
      cur.cam.lerp(scratch.cam, k);
      cur.look.lerp(scratch.look, k);
      cur.bx += (scratch.bx - cur.bx) * k;
      cur.key += (scratch.key - cur.key) * k;
      cur.rim += (scratch.rim - cur.rim) * k;
      cur.amb += (scratch.amb - cur.amb) * k;
      cur.kc.lerp(scratch.kc, k);
      cur.bg.lerp(scratch.bg, k);
      cur.sil += (scratch.sil - cur.sil) * k;
      silOpacity.current = cur.sil;

      // restrained mouse parallax (a few degrees max)
      const kp = 1 - Math.exp(-2.6 * dt);
      par.current.x += (mouseRef.current.x * 0.24 - par.current.x) * kp;
      par.current.y += (-mouseRef.current.y * 0.14 - par.current.y) * kp;

      camera.position.set(
        cur.cam.x + par.current.x,
        cur.cam.y + par.current.y,
        cur.cam.z
      );
      camera.lookAt(cur.look);

      // rotation: slow idle spin at the hero, choreographed through the
      // chapters. Damping to the nearest 2π-equivalent target keeps it
      // perfectly continuous scrolling forward or backward.
      if (p < 0.02) {
        rotState.current += dt * IDLE_SPEED;
      } else {
        let target = scratch.rot;
        target += Math.round((rotState.current - target) / TWO_PI) * TWO_PI;
        rotState.current += (target - rotState.current) * k;
      }
      if (bottleRef.current) {
        bottleRef.current.rotation.y = rotState.current;
        bottleRef.current.position.x = cur.bx;
      }
    }

    if (keyRef.current) {
      keyRef.current.intensity = cur.key;
      keyRef.current.color.copy(cur.kc);
    }
    if (rimRef.current) rimRef.current.intensity = cur.rim;
    if (ambRef.current) ambRef.current.intensity = cur.amb;
    if (scene.background && scene.background.isColor) scene.background.copy(cur.bg);
  });

  return (
    <>
      <group ref={bottleRef}>
        <WineBottle isMobile={isMobile} />
        <ContactShadow />
      </group>
      {!staticMode && <SilhouetteBottles opacityRef={silOpacity} />}

      {/* warm key light */}
      <directionalLight ref={keyRef} position={[3.5, 4.5, 2.5]} intensity={0.4} color="#ffd9a8" />
      {/* rim light from behind — reveals silhouette + burgundy translucency */}
      <directionalLight ref={rimRef} position={[-3, 2.4, -4.5]} intensity={2.4} color="#f7e8d8" />
      <ambientLight ref={ambRef} intensity={0.03} color="#cbb0a6" />

      {/* dark cellar environment built from lightformers — no network HDR */}
      <Environment resolution={256} frames={1}>
        <color attach="background" args={["#0a0708"]} />
        <Lightformer intensity={1.6} position={[3, 3.5, 4]} scale={[3.5, 2, 1]} color="#ffe6c4" form="rect" />
        <Lightformer intensity={0.9} position={[-4.5, 2.5, -2]} scale={[1.6, 5, 1]} color="#f7f1e7" form="rect" />
        <Lightformer intensity={0.35} position={[0, 6, 0]} rotation-x={Math.PI / 2} scale={[7, 7, 1]} color="#c7a45d" form="rect" />
        <Lightformer intensity={0.35} position={[0, 1.2, -6]} scale={[8, 2.5, 1]} color="#4B0F1E" form="rect" />
      </Environment>

      {/* dark glossy floor with a soft environment sheen */}
      <mesh rotation-x={-Math.PI / 2} position-y={-0.002}>
        <circleGeometry args={[30, 64]} />
        <meshStandardMaterial color="#0a0a0a" metalness={0} roughness={0.9} envMapIntensity={0.06} />
      </mesh>
    </>
  );
};

const SceneReady = ({ onReady }) => {
  const count = useRef(0);
  const fired = useRef(false);
  useFrame(() => {
    // fire only after real frames have been presented — the preloader can
    // never reveal a blank canvas, even on slow GPUs
    if (!fired.current && ++count.current >= 3) {
      fired.current = true;
      onReady && onReady();
    }
  });
  return null;
};

export const BottleScene = ({ progressRef, mouseRef, isMobile, staticMode = false, onReady }) => {
  return (
    <Canvas
      frameloop={staticMode ? "demand" : "always"}
      dpr={[1, isMobile ? 1.5 : 2]}
      camera={{ fov: 34, near: 0.1, far: 60, position: [0, 0.7, 8.4] }}
      gl={{ antialias: true, alpha: false, powerPreference: "high-performance" }}
      onCreated={({ scene }) => {
        scene.background = new THREE.Color("#0e0e0e");
      }}
      style={{ width: "100%", height: "100%" }}
    >
      <Rig
        progressRef={progressRef}
        mouseRef={mouseRef}
        isMobile={isMobile}
        staticMode={staticMode}
      />
      <SceneReady onReady={onReady} />
    </Canvas>
  );
};
